import { useState } from 'react';

interface SocialShareProps {
  url?: string;
  title?: string;
  description?: string;
}

export default function SocialShare({ url, title, description }: SocialShareProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const shareUrl = url || window.location.href;
  const shareTitle = title || document.title;
  const shareDescription = description || 'Check out this amazing content!';

  const handleShare = (platform: string) => {
    let shareLink = '';
    
    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'pinterest':
        shareLink = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareTitle)}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`;
        break;
      case 'email':
        shareLink = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareDescription + '\n\n' + shareUrl)}`;
        break;
    }

    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-700">Share:</span>
      
      <button
        onClick={() => handleShare('facebook')}
        className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label="Share on Facebook"
      >
        <i className="ri-facebook-fill text-base"></i>
      </button>

      <button
        onClick={() => handleShare('twitter')}
        className="w-9 h-9 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label="Share on Twitter"
      >
        <i className="ri-twitter-x-fill text-base"></i>
      </button>

      <button
        onClick={() => handleShare('linkedin')}
        className="w-9 h-9 rounded-full bg-blue-700 hover:bg-blue-800 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label="Share on LinkedIn"
      >
        <i className="ri-linkedin-fill text-base"></i>
      </button>

      <button
        onClick={() => handleShare('pinterest')}
        className="w-9 h-9 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label="Share on Pinterest"
      >
        <i className="ri-pinterest-fill text-base"></i>
      </button>

      <button
        onClick={() => handleShare('whatsapp')}
        className="w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label="Share on WhatsApp"
      >
        <i className="ri-whatsapp-fill text-base"></i>
      </button>

      <button
        onClick={() => handleShare('email')}
        className="w-9 h-9 rounded-full bg-gray-600 hover:bg-gray-700 flex items-center justify-center text-white transition-colors cursor-pointer"
        aria-label="Share via Email"
      >
        <i className="ri-mail-fill text-base"></i>
      </button>

      <div className="relative">
        <button
          onClick={handleCopyLink}
          className="w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 transition-colors cursor-pointer"
          aria-label="Copy Link"
        >
          <i className="ri-link text-base"></i>
        </button>
        
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap">
            Link copied!
          </div>
        )}
      </div>
    </div>
  );
}
